import type * as zod from 'zod';

import { FailureKind } from '@sb/routine-runner/FailureKind';
import type { StepFailure } from '@sb/routine-runner/types';

import type { StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments from './Arguments';
import Variables from './Variables';

type Arguments = zod.infer<typeof Arguments>;

type Variables = zod.infer<typeof Variables>;

/**
 * ElseIf step executes its substeps if the previous If/ElseIf condition was false
 * and this condition is true.
 *
 * The block coordination (skipping when previous condition was true) is handled
 * by RoutineRunner.playSteps using instanceof checks and the wasConditionSatisfied() method.
 */
export default class ElseIfStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = true;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public substeps: Array<Step<object, object>> = [];

  /** Tracks whether the condition was satisfied (substeps ran) */
  private conditionWasSatisfied = false;

  public initializeVariableState(): void {
    this.variables = {};
    this.conditionWasSatisfied = false;
  }

  /** Returns true if condition was true and substeps were executed */
  public wasConditionSatisfied(): boolean {
    return this.conditionWasSatisfied;
  }

  public async evaluateCondition(
    onFail: (failure: StepFailure) => Promise<void>,
  ): Promise<boolean> {
    try {
      const result = await this.routineContext.evaluateConditional(
        this.args.condition,
      );

      return Boolean(result);
    } catch (error) {
      await onFail({
        failure: {
          kind: FailureKind.InvalidRoutineLoadedFailure,
        },
        failureReason: error.message,
        error,
      });

      return false;
    }
  }

  public async _play({ playSubSteps, fail }: StepPlayArguments): Promise<void> {
    // Note: Block coordination (skipping if previous condition was satisfied)
    // is handled by RoutineRunner.playSteps before this method is called.
    const shouldRunSteps = await this.evaluateCondition(fail);

    if (await super.shouldStopPlaying()) {
      return;
    }

    this.conditionWasSatisfied = shouldRunSteps;

    if (shouldRunSteps) {
      await playSubSteps();
    }
  }
}
